import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isDemoAccount } from '../lib/auth';
import { Loader } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'landlord';
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [hasRequiredRole, setHasRequiredRole] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    checkAuth();
  }, [retryCount]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setIsAuthenticated(false);
        setHasRequiredRole(false);
        setLoading(false);
        return;
      }
      
      setIsAuthenticated(true);
      
      // If no specific role is required, we're done
      if (!requiredRole) {
        setHasRequiredRole(true);
        setLoading(false);
        return;
      }
      
      // Check user role
      const { data: userData } = await supabase.auth.getUser();
      const userRole = userData?.user?.user_metadata?.role || 'user';
      
      // For demo accounts, allow access to all routes
      if (isDemoAccount(userData?.user?.email)) {
        if (requiredRole === 'admin' && userData?.user?.email === 'admin@example.com') {
          setHasRequiredRole(true);
        } else if (requiredRole === 'landlord' && userData?.user?.email === 'landlord@example.com') {
          setHasRequiredRole(true);
        } else if (userData?.user?.email === 'admin@example.com') {
          // Admin demo account can access all routes
          setHasRequiredRole(true);
        } else {
          setHasRequiredRole(false);
        }
      } else {
        // If admin role is required, check if user is admin
        if (requiredRole === 'admin') {
          setHasRequiredRole(userRole === 'admin');
        } 
        // If landlord role is required, check if user is landlord
        else if (requiredRole === 'landlord') {
          setHasRequiredRole(userRole === 'landlord');
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error checking auth:', error);
      
      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const timeout = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, timeout);
      } else {
        setIsAuthenticated(false);
        setHasRequiredRole(false);
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }

  if (requiredRole && !hasRequiredRole) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}